import React from "react";
import {
  Box,
  Toolbar,
  AppBar,
  Button,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import Dashboard from "../assets/images/trello.png";
import { useNavigate, useLocation } from "react-router-dom";
import { authUtils } from "@/utils/auth";
import logo from "@/assets/images/eb_logo.png";


/**
 * Navigation menu items configuration
 * @constant {Array<{text: string, icon: React.ReactNode, path: string}>}
 */
type MenuItem = {
  text: string;
  icon?: React.ReactNode;   // <-- allow JSX here
  path: string;
};

const menuItems: MenuItem[] = [
  {
    text: "Incoming Messages",
    icon: <img src={Dashboard} alt="Dashboard" style={{ width: 24, height: 24 }} />,
    path: "/dashboard",
  },
  {
    text: "Email Process Settings",
    icon: <img src={Dashboard} alt="email" style={{ width: 24, height: 24 }} />,
    path: "/email-process-settings",
  },
];

/**
 * Props for the DashboardLayout component
 * @interface DashboardLayoutProps
 * @property {React.ReactNode} children - The child components to be rendered in the main content area
 */
interface DashboardLayoutProps {
  children: React.ReactNode;
}

/**
 * A responsive layout component that provides a sidebar navigation menu.
 * Automatically switches between mobile and desktop views based on screen size.
 * 
 * @component
 * @param {DashboardLayoutProps} props - The component props
 * @returns {JSX.Element} The layout component with responsive drawer and main content area
 * 
 * @example
 * <DashboardLayout>
 *   <YourPageComponent />
 * </DashboardLayout>
 */
const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Handles navigation to different routes
   * @function handleNavigation
   * @param {string} path - The route path to navigate to
   * @returns {void}
   */
  const handleNavigation = (path: string): void => {
    navigate(path);
  };

  /**
   * Handles user logout by removing the authentication token and redirecting to logout page
   * @function handleLogout
   * @returns {void}
   */
  const handleLogout = (): void => {
    console.log("DashboardLayout: Logging out user");
    authUtils.removeToken();
    authUtils.clearIdleExpiry();
    navigate("/logout");
  };

  const userEmail = localStorage.getItem("user_name") || "";
  const userInitials = React.useMemo(() => {
    const local = userEmail.split("@")[0] || "";
    const parts = local.split(/[._-]+/).filter(Boolean);
    const first = parts[0]?.[0] ?? "";
    const second = parts[1]?.[0] ?? "";
    return (first + second).toUpperCase() || "S";
  }, [userEmail]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#1b184d",
          boxShadow: "none",
          height: 64,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            minHeight: 64,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <img
              src={logo}
              alt="EB Industries"
              style={{ height: 40, objectFit: "cover" }}
            />
            <Box sx={{ textAlign: "left",marginLeft:'10px' }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "1rem",
                  lineHeight: 1.2,
                }}
              >
                EB Industries
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "0.65rem",
                  letterSpacing: 1,
                  lineHeight: 1,
                  textTransform: "uppercase",
                }}
              >
                Accounts & Order Automation
              </Typography>
            </Box>
            <Box>
                 {menuItems.map((item) => (
              <Button
                key={item.text}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  color: "#fff",
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: location.pathname === item.path ? 700 : 400,
                  borderBottom:
                    location.pathname === item.path ? "2px solid #fff" : "none",
                  borderRadius: 0,
                  px: 1,
                  py: 2,
                  minWidth: "auto",
                  "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
                }}
              >
                {item.text}
              </Button>
            ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
           
            <Tooltip title={userEmail} arrow placement="bottom">
              <IconButton
                onClick={(event) => setAnchorEl(event.currentTarget)}
                size="small"
                edge="end"
              >
                <Avatar
                  sx={{
                    bgcolor: "#fff",
                    color: "#1b184d",
                    width: 32,
                    height: 32,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                  }}
                >
                  {userInitials}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MuiMenuItem
                onClick={() => {
                  setAnchorEl(null);
                  handleLogout();
                }}
              >
                Logout
              </MuiMenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: "64px",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
